import React from "react";
import PropTypes from "prop-types";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import StarIcon from "@mui/icons-material/Star";
import VerifiedIcon from "@mui/icons-material/Verified";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const GoogleGIcon = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
  </svg>
);

GoogleGIcon.defaultProps = {
  size: 28
};

GoogleGIcon.propTypes = {
  size: PropTypes.number
};

const defaultGoogleReviews = [
  {
    name: "Select Design by Diana Șucu",
    role: "Partener Comercial",
    review: "Colaborarea cu Doral Cleaning a început de la deschiderea magazinului nostru. Serviciile de curățenie oferite s-au îndreptat ulterior și către clienții noștri, prin curățarea carpetelor, a covoarelor și prin întreținerea articolelor de mobilier. Recomandăm cu încredere serviciile Doral Cleaning.",
    rating: 5,
    date: "Testimonial client"
  },
  {
    name: "Andrada Ciubotariu",
    role: "Office Manager - ENTEX LOGIC S.R.L",
    review: "Apreciem profesionalismul și constanța echipei Doral Cleaning. Serviciile de curățenie sunt livrate regulat și eficient, menținând spațiile noastre curate și organizate, adaptate nevoilor companiei noastre.",
    rating: 5,
    date: "Testimonial client"
  },
  {
    name: "Florentina Șerban",
    role: "URSA Romania SRL",
    review: "Colaborăm cu compania Doral Cleaning din anul 2014 și suntem mulțumiți de serviciile lor de curățenie. Ne face plăcere să recomandăm această companie pentru seriozitate, promptitudine și profesionalism.",
    rating: 5,
    date: "Testimonial client"
  },
  {
    name: "Doru Leonte",
    role: "TSG – Transport Service Group",
    review: "În cei 15 ani de colaborare, am avut partenerul potrivit proiectelor noastre, care a răspuns de fiecare dată prompt și a asigurat constant servicii de calitate prin servicii de întreținere zilnică, generale periodice, reamenajări și relocări. Recomandăm cu încredere Doral Cleaning pentru seriozitate și implicare pe termen lung.",
    rating: 5,
    date: "Testimonial client"
  },
  {
    name: "Florin Almășanu",
    role: "SuperNova – Lujerului, Alexandriei, Constanța și Drobeta-Turnu Severin",
    review: "Recomand cu multă căldură serviciile oferite de Doral, mai ales pentru strategiile de logistică și de execuție a echipelor de intervenție și alpinism. Au fost mereu la înălțimea așteptărilor noastre prin cele mai bune servicii de spălare a fațadelor, spălări automatizate ale pardoselilor, curățarea structurilor metalice, a galeriilor și a parcărilor subterane.",
    rating: 5,
    date: "Testimonial client"
  }
];

export default function GoogleTrustIndex({ title, subtitle, reviews }) {
  const googleBusinessLink = "https://g.page/r/Ca-KPqkQ9DosEAE/review";
  const googleMapsLink = "https://maps.app.goo.gl/ZvLDzHRXk4KXUUwS6";

  return (
    <MKBox component="section" py={4}>
      <Container>
        {/* TrustIndex Header Badge */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            borderRadius: "20px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
            p: { xs: 3, md: 4 },
            mb: 5,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 3
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "16px",
                backgroundColor: "#ffffff",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <GoogleGIcon size={32} />
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="h6" fontWeight="700" color="#0f172a" sx={{ fontSize: "1.1rem" }}>
                  Google Business Profile
                </Typography>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: "#ecfdf5",
                    color: "#059669",
                    px: 1.2,
                    py: 0.3,
                    borderRadius: "20px",
                    fontSize: "0.75rem",
                    fontWeight: 700
                  }}
                >
                  <VerifiedIcon sx={{ fontSize: 14 }} /> Profil Verificat
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <Typography variant="h5" fontWeight="800" color="#0f172a">
                  5.0
                </Typography>
                <Box sx={{ display: "flex", color: "#f59e0b" }}>
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} sx={{ fontSize: 20 }} />
                  ))}
                </Box>
                <Typography variant="body2" color="#64748b" sx={{ fontSize: "0.85rem", ml: 0.5 }}>
                  (Evaluat excelent de clienți)
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: { xs: "center", md: "flex-end" } }}>
            <Button
              component="a"
              href={googleBusinessLink}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              sx={{
                backgroundColor: "#1a73e8",
                color: "#ffffff",
                fontWeight: 700,
                fontSize: "0.85rem",
                borderRadius: "12px",
                px: 2.5,
                py: 1.2,
                boxShadow: "0 4px 12px rgba(26, 115, 232, 0.25)",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1557b0"
                }
              }}
              startIcon={<GoogleGIcon size={18} />}
              endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            >
              Lasă o recenzie pe Google
            </Button>

            <Button
              component="a"
              href={googleMapsLink}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={{
                borderColor: "#cbd5e1",
                color: "#334155",
                fontWeight: 600,
                fontSize: "0.85rem",
                borderRadius: "12px",
                px: 2,
                py: 1.2,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#94a3b8",
                  backgroundColor: "#f8fafc"
                }
              }}
            >
              Vezi pe Google Maps
            </Button>
          </Box>
        </Box>

        {/* Section Titles if provided */}
        {(title || subtitle) && (
          <Box textAlign="center" mb={4}>
            {title && (
              <MKTypography variant="h2" color="dark" mb={1}>
                {title}
              </MKTypography>
            )}
            {subtitle && (
              <MKTypography variant="body1" color="text">
                {subtitle}
              </MKTypography>
            )}
          </Box>
        )}

        {/* Reviews Carousel */}
        <Swiper
          modules={[Autoplay, Pagination, Navigation]}
          autoplay={{ delay: 5500, disableOnInteraction: false }}
          loop={true}
          spaceBetween={24}
          slidesPerView={1}
          pagination={{ clickable: true }}
          navigation={true}
          breakpoints={{
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 }
          }}
          style={{ paddingBottom: "48px" }}
        >
          {reviews.map((rev, idx) => (
            <SwiperSlide key={idx} style={{ height: "auto" }}>
              <Box
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  p: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  border: "1px solid #f1f5f9",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 28px rgba(0, 0, 0, 0.08)"
                  }
                }}
              >
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #1a73e8, #34a853)",
                          color: "#ffffff",
                          fontWeight: 700,
                          fontSize: "1rem",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textTransform: "uppercase"
                        }}
                      >
                        {rev.name.charAt(0)}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="700" color="#0f172a" sx={{ lineHeight: 1.2 }}>
                          {rev.name}
                        </Typography>
                        {rev.role && (
                          <Typography variant="caption" color="#64748b" sx={{ display: "block", fontSize: "0.75rem" }}>
                            {rev.role}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <GoogleGIcon size={20} />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <Box sx={{ display: "flex", color: "#f59e0b" }}>
                      {[...Array(rev.rating || 5)].map((_, i) => (
                        <StarIcon key={i} sx={{ fontSize: 18 }} />
                      ))}
                    </Box>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.3,
                        color: "#059669",
                        fontSize: "0.7rem",
                        fontWeight: 700
                      }}
                    >
                      <VerifiedIcon sx={{ fontSize: 12 }} /> Verificat
                    </Box>
                  </Box>

                  <Typography
                    variant="body2"
                    color="#334155"
                    sx={{
                      lineHeight: 1.6,
                      fontSize: "0.9rem",
                      fontStyle: "normal",
                      textWrap: "pretty"
                    }}
                  >
                    {`"${rev.review}"`}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2.5, pt: 1.5, borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="caption" color="#94a3b8" sx={{ fontSize: "0.75rem" }}>
                    {rev.date || "Recenzie Google"}
                  </Typography>
                  <Typography variant="caption" color="#1a73e8" fontWeight="600" sx={{ fontSize: "0.75rem" }}>
                    Google Business
                  </Typography>
                </Box>
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
      </Container>
    </MKBox>
  );
}

GoogleTrustIndex.defaultProps = {
  title: "",
  subtitle: "",
  reviews: defaultGoogleReviews
};

GoogleTrustIndex.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      role: PropTypes.string,
      review: PropTypes.string.isRequired,
      rating: PropTypes.number,
      date: PropTypes.string
    })
  )
};
